import { Body, Caption, RadioGroup, RadioGroupItem, Stack } from '@leanin/ui';

export type ReasonOption<V extends string> = {
  value: V;
  label: string;
  /** Second, muted line under the label. Omit for a label-only option. */
  description?: string;
};

// The required "why" radio list shared by the moderation dialogs — a member
// reporting content and a leader removing it. Each owns its own reasons and
// copy; the option rendering stays one shape. Labels read at body weight,
// not the radio primitive's bold default, so a four-option list reads as
// choices rather than headings.
export function ReasonRadioGroup<V extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: ReadonlyArray<ReasonOption<V>>;
  value: V | null;
  onChange: (value: V) => void;
  ariaLabel: string;
}) {
  return (
    <RadioGroup
      value={value ?? ''}
      onValueChange={(v) => {
        const picked = options.find((o) => o.value === v);
        if (picked !== undefined) onChange(picked.value);
      }}
      aria-label={ariaLabel}
    >
      <Stack gap="lg">
        {options.map((opt) => (
          <RadioGroupItem key={opt.value} value={opt.value}>
            <Stack gap="xs">
              <Body>{opt.label}</Body>
              {opt.description !== undefined ? <Caption>{opt.description}</Caption> : null}
            </Stack>
          </RadioGroupItem>
        ))}
      </Stack>
    </RadioGroup>
  );
}
